// auth.js - Unified Keycloak Authentication & Token Management with Hybrid Storage

import Keycloak from "keycloak-js";
import { jwtDecode } from "jwt-decode";

// Check if Keycloak configuration is available
const isKeycloakConfigured = () => {
  return !!(
    process.env.REACT_APP_KEYCLOAK_URL &&
    process.env.REACT_APP_KEYCLOAK_REALM &&
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID
  );
};

// Initialize Keycloak
const keycloak = isKeycloakConfigured()
  ? new Keycloak({
      url: process.env.REACT_APP_KEYCLOAK_URL,
      realm: process.env.REACT_APP_KEYCLOAK_REALM,
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
    })
  : null;

// In-memory storage (not accessible via XSS)
let inMemoryToken = null;
let inMemoryRefreshToken = null;
let userIdInMemory = null;
let sessionTimeoutId = null;
let initializationAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;
// Globally accessible, in-memory user snapshot
let currentUserSnapshot = null;

// Initialize Keycloak on app startup
let hasInitializedKeycloak = false;
export const initKeycloak = async () => {
  try {
    if (hasInitializedKeycloak) {
      // Already initialized in this runtime
      return true;
    }
    // Check if Keycloak is configured
    if (!isKeycloakConfigured()) {
      console.warn(
        "Keycloak is not configured. Please set REACT_APP_KEYCLOAK_URL, REACT_APP_KEYCLOAK_REALM, and REACT_APP_KEYCLOAK_CLIENT_ID environment variables."
      );
      return false;
    }

    if (!keycloak) {
      console.error("Keycloak instance is not available");
      return false;
    }

    // Clear any existing tokens
    inMemoryToken = null;
    inMemoryRefreshToken = null;
    userIdInMemory = sessionStorage.getItem("userId");

    // Add retry logic for initialization
    while (initializationAttempts < MAX_INIT_ATTEMPTS) {
      try {
        const authenticated = await keycloak.init({
          onLoad: "check-sso",
          pkceMethod: "S256",
          checkLoginIframe: false,
          checkLoginIframeInterval: 0, // Disable iframe checking completely
          token: inMemoryToken,
          refreshToken: inMemoryRefreshToken,
          // Remove silentCheckSsoRedirectUri to avoid iframe issues
          // Use popup for silent SSO instead of iframe
          silentCheckSsoFallback: true,
        });

        if (authenticated) {
          inMemoryToken = keycloak.token;
          inMemoryRefreshToken = keycloak.refreshToken;

          const tokenData = jwtDecode(keycloak.token);
          if (tokenData && tokenData.sub) {
            userIdInMemory = tokenData.sub;
            sessionStorage.setItem("userId", tokenData.sub);
          }

          setupSessionTimeout();
          initializationAttempts = 0; // Reset counter on successful init
          hasInitializedKeycloak = true;
          return true;
        } else {
          console.warn("User not authenticated, redirecting to login...");
          // Clear any stale data
          sessionStorage.removeItem("userId");
          await keycloak.login();
          return false;
        }
      } catch (error) {
        initializationAttempts++;
        console.error(
          `Keycloak initialization attempt ${initializationAttempts} failed:`,
          error
        );

        if (initializationAttempts >= MAX_INIT_ATTEMPTS) {
          console.error(
            "Maximum initialization attempts reached. Keycloak may be unavailable."
          );
          // Don't throw error, just return false to allow graceful degradation
          return false;
        }

        // Wait before retrying with exponential backoff
        const delay = Math.min(
          1000 * Math.pow(2, initializationAttempts - 1),
          10000
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // If we exit the loop without returning, something went wrong
    console.error("Keycloak initialization failed after all attempts");
    return false;
  } catch (error) {
    console.error(
      "Keycloak initialization failed with unexpected error:",
      error
    );
    // Clear any potentially corrupted state
    inMemoryToken = null;
    inMemoryRefreshToken = null;
    userIdInMemory = null;
    sessionStorage.removeItem("userId");
    return false;
  }
};

// Session timeout management
const setupSessionTimeout = () => {
  // Clear any existing timeout
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }

  // Set timeout to 30 minutes (adjust as needed)
  const sessionTimeout = 30 * 60 * 1000;
  sessionTimeoutId = setTimeout(() => {
    logout();
  }, sessionTimeout);
};

// Reset session timeout on user activity
// Centralize the logic for resetting the session timeout and can add additional functionality in the future without modifying the event listeners.
export const resetSessionTimeout = () => {
  setupSessionTimeout();
};

// Add event listeners for user activity
if (typeof window !== "undefined") {
  ["mousedown", "keypress", "scroll", "touchstart"].forEach((event) => {
    window.addEventListener(event, resetSessionTimeout, false);
  });
}

// User ID management
export const getuserId = () => {
  // First try memory
  if (userIdInMemory) {
    return userIdInMemory;
  }

  // Fall back to sessionStorage
  return sessionStorage.getItem("userId");
};

// Login Function
export const login = async () => {
  try {
    if (!keycloak) {
      throw new Error("Keycloak is not available");
    }

    // Clear any existing tokens before login
    inMemoryToken = null;
    inMemoryRefreshToken = null;
    userIdInMemory = null;
    sessionStorage.removeItem("userId");

    await keycloak.login();
  } catch (error) {
    console.error("Login failed:", error);
    throw error; // Propagate the error to handle it in the UI
  }
};

// Logout Function
export const logout = async () => {
  try {
    if (keycloak) {
      await keycloak.logout();
    }

    // Clear tokens and userId
    inMemoryToken = null;
    inMemoryRefreshToken = null;
    userIdInMemory = null;

    // Clear sessionStorage
    sessionStorage.removeItem("userId");

    // Clear session timeout
    if (sessionTimeoutId) {
      clearTimeout(sessionTimeoutId);
    }
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

// Get Valid Token (Refresh if needed)
export const getToken = async () => {
  try {
    if (!keycloak) {
      console.warn("Keycloak is not available");
      return null;
    }

    if (!keycloak.token) {
      console.warn("No token available, redirecting to login");
      await login();
      return null;
    }

    if (keycloak.isTokenExpired()) {
      console.warn("Token expired, refreshing...");
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          inMemoryToken = keycloak.token;
          inMemoryRefreshToken = keycloak.refreshToken;
          setupSessionTimeout();
        } else {
          console.warn("Token refresh failed, redirecting to login");
          await login();
          return null;
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        if (refreshError.error === "invalid_grant") {
          await login();
        }
        return null;
      }
    }
    return keycloak.token;
  } catch (error) {
    console.error("Error in getToken:", error);
    return null;
  }
};

// Decode Token and Get User Info
export const getUserProfile = async () => {
  try {
    const token = await getToken();
    if (!token) return null;

    const decodedToken = jwtDecode(token);

    const keycloakEnv = process.env.REACT_APP_KEYCLOAK_ENVIRONMENT;

    // Validate KEYCLOAK_ENVIRONMENT variable
    if (!keycloakEnv) {
      console.warn(
        "REACT_APP_KEYCLOAK_ENVIRONMENT is not set. Client roles may not be extracted properly."
      );
    }

    const keycloakRoles = decodedToken.realm_access?.roles || [];
    const resourceRoles =
      decodedToken.resource_access?.[keycloakEnv]?.roles || [];
    const groups = decodedToken.groups || [];
    const permissions = decodedToken.permissions || [];

    const profile = {
      id: decodedToken.sub,
      email: decodedToken.email || "",
      firstName: decodedToken.given_name || "",
      lastName: decodedToken.family_name || "",
      fullName: decodedToken.name || "",
      username: decodedToken.preferred_username || "",
      roles: Array.from(
        new Set([...(keycloakRoles || []), ...(resourceRoles || [])])
      ),
      groups,
      permissions,
      rawToken: decodedToken,
    };

    // Update global snapshot
    currentUserSnapshot = profile;
    return profile;
  } catch (error) {
    console.error("Error getting user profile:", error);
    return null;
  }
};

// Identifier of the current Keycloak SSO session.
// Used to fire per-session side effects (such as the login ping) exactly once,
// because check-sso resolves on every page load, not only on a fresh login.
// Realms that do not emit `sid` fall back to the authentication time, which
// changes on re-authentication for the same reason.
export const getSessionId = async () => {
  try {
    const token = await getToken();
    if (!token) return null;

    const decodedToken = jwtDecode(token);
    if (decodedToken.sid) return decodedToken.sid;
    return decodedToken.auth_time ? `auth-${decodedToken.auth_time}` : null;
  } catch (error) {
    console.error("Error reading session id:", error);
    return null;
  }
};

// Helper function to get cookie value
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

// Accessor for global user snapshot (read-only)
export const getCurrentUser = () => currentUserSnapshot;

export default keycloak;
